/**
 * How long a rendered CMS page, the blog's or the academy's, may be reused, and
 * by whom.
 *
 * The number is not free-standing: `blog.ts` and `academy.ts` already hold
 * fetched items in process for this long, so a shared cache that holds the
 * rendered page for the same span adds no staleness the app did not already
 * have. Stating it once is what stops them drifting when one is edited alone.
 */
export const CONTENT_TTL_SECONDS = 300;

/**
 * Shared caches only.
 *
 * `max-age=0` is deliberate. An editor who publishes in the CMS and reloads has
 * to see the change once the shared cache expires; a browser `max-age` would
 * pin the old page per device for its whole life and nothing we own could purge
 * it, where a CDN entry is simply re-fetched.
 *
 * `stale-while-revalidate` is what keeps the CMS off a reader's critical path:
 * past the TTL the edge serves the previous render and refreshes behind it, so
 * the cost of a miss is a little staleness rather than a CMS round trip.
 * `stale-if-error` is what makes a CMS outage invisible: the edge keeps serving
 * the last good render for a day. Blog and academy have no snapshot to fall
 * through to, so it is the only thing between an outage and an error page.
 * Both are extensions, so a cache without them ignores them.
 */
const sharedCachePolicy = (ttlSeconds: number) =>
  `public, max-age=0, s-maxage=${ttlSeconds}, stale-while-revalidate=${ttlSeconds}, stale-if-error=86400`;

export const CONTENT_CACHE_CONTROL = sharedCachePolicy(CONTENT_TTL_SECONDS);

/**
 * A missing item is the one response that must not be held. The `$slug` routes
 * answer a real 404 for an unpublished or renamed slug, and a listing links to
 * an item the moment it appears in the CMS; reusing the policy above would let
 * a five-minute-old 404 outlive the publish that fixed it.
 */
export const CONTENT_NOT_FOUND_CACHE_CONTROL = "public, max-age=0, s-maxage=0";
